import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toIso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function AdminCalendar({ bookings, statusStyles, onSelect }) {
    const [weekOffset, setWeekOffset] = useState(0);

    const days = useMemo(() => {
        const start = new Date();
        const mondayOffset = (start.getDay() + 6) % 7;
        start.setDate(start.getDate() - mondayOffset + weekOffset * 7);
        start.setHours(0, 0, 0, 0);
        return Array.from({ length: 7 }, (_, i) => {
            const d = new Date(start);
            d.setDate(d.getDate() + i);
            return d;
        });
    }, [weekOffset]);

    const byDate = useMemo(() => {
        const map = {};
        bookings.forEach((b) => {
            (map[b.date] = map[b.date] || []).push(b);
        });
        return map;
    }, [bookings]);

    const todayIso = toIso(new Date());
    const rangeLabel = `${days[0].toLocaleDateString("en-AU", { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}`;

    return (
        <div data-testid="admin-calendar-view">
            <div className="flex items-center justify-between mb-6">
                <button
                    onClick={() => setWeekOffset((w) => w - 1)}
                    data-testid="calendar-prev-week-button"
                    className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 hover:text-amber-400 border border-[#383E4C] rounded-full px-4 py-2 transition-colors duration-200"
                >
                    <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <div className="text-sm font-mono text-zinc-300 uppercase tracking-widest" data-testid="calendar-range-label">
                    {rangeLabel}
                </div>
                <button
                    onClick={() => setWeekOffset((w) => w + 1)}
                    data-testid="calendar-next-week-button"
                    className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-[0.15em] text-zinc-400 hover:text-amber-400 border border-[#383E4C] rounded-full px-4 py-2 transition-colors duration-200"
                >
                    Next <ChevronRight className="w-4 h-4" />
                </button>
            </div>

            <div className="overflow-x-auto">
                <div className="grid grid-cols-7 gap-3 min-w-[840px]">
                    {days.map((day, i) => {
                        const iso = toIso(day);
                        const dayBookings = byDate[iso] || [];
                        const isToday = iso === todayIso;
                        return (
                            <div
                                key={iso}
                                data-testid={`calendar-day-${iso}`}
                                className={`rounded-xl border p-3 min-h-[180px] ${
                                    isToday ? "border-amber-500/50 bg-amber-500/5" : "border-[#22262F] bg-[#121418]"
                                }`}
                            >
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500">
                                        {DAY_NAMES[i]}
                                    </span>
                                    <span
                                        className={`text-sm font-display font-bold ${
                                            isToday ? "text-amber-400" : "text-zinc-300"
                                        }`}
                                    >
                                        {day.getDate()}
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {dayBookings.map((b) => (
                                        <button
                                            key={b.id}
                                            onClick={() => onSelect(b)}
                                            data-testid={`calendar-booking-${b.id}`}
                                            className={`w-full text-left rounded-lg border px-2.5 py-2 hover:brightness-125 transition-[filter] duration-150 ${statusStyles[b.status] || "border-[#383E4C] text-zinc-300"}`}
                                        >
                                            <div className="text-[10px] font-mono">{b.id}</div>
                                            <div className="text-xs font-semibold truncate">{b.customer_name}</div>
                                            <div className="text-[10px] opacity-70 truncate">{b.time_slot.split(" - ")[0]}</div>
                                        </button>
                                    ))}
                                    {dayBookings.length === 0 && (
                                        <div className="text-[10px] font-mono text-zinc-700 uppercase tracking-widest pt-2">
                                            Free
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
